import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { api, formatBytes, formatDate } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Envelope,
  EnvelopeOpen,
  UploadSimple,
  MagnifyingGlass,
  Folder,
  FolderOpen,
  House,
  CaretRight,
  CaretDown,
  CaretUp,
  CaretUpDown,
  DownloadSimple,
  Eye,
  Trash,
  DotsThreeVertical,
  Paperclip,
  User,
  Users,
  Calendar,
  Check,
  X,
  FilePdf,
  FileText,
  FileImage,
  File as FileIcon,
  Archive,
  Info,
  CheckCircle
} from '@phosphor-icons/react';

// Build folder tree from flat array
const buildFolderTree = (folders) => {
  const folderMap = {};
  const roots = [];
  
  folders.forEach(folder => {
    folderMap[folder.id] = { ...folder, children: [] };
  });
  
  folders.forEach(folder => {
    if (folder.parent_id && folderMap[folder.parent_id]) {
      folderMap[folder.parent_id].children.push(folderMap[folder.id]);
    } else {
      roots.push(folderMap[folder.id]);
    }
  });
  
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach(node => {
      if (node.children.length > 0) sortChildren(node.children);
    });
  };
  
  sortChildren(roots);
  return roots;
};

// Folder Tree Dialog Component
const FolderTreeDialog = ({ open, onClose, folders, selectedFolder, onSelect, rootLabel, rootValue = 'none', t }) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [tempSelected, setTempSelected] = useState(selectedFolder);
  const treeData = useMemo(() => buildFolderTree(folders), [folders]);
  
  useEffect(() => {
    if (open) setTempSelected(selectedFolder);
  }, [open, selectedFolder]);
  
  const toggleExpand = (folderId, e) => {
    e.stopPropagation();
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) newSet.delete(folderId);
      else newSet.add(folderId);
      return newSet;
    });
  };
  
  const handleConfirm = () => {
    onSelect(tempSelected);
    onClose();
  };
  
  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expandedFolders.has(node.id);
    const isSelected = tempSelected === node.id;
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div key={node.id}>
        <div
          className={`flex items-center gap-2 py-2 px-3 cursor-pointer rounded-sm transition-colors ${
            isSelected ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F4F4F5]'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => setTempSelected(node.id)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => toggleExpand(node.id, e)}
              className={`p-0.5 rounded-sm ${isSelected ? 'hover:bg-[#001d6a]' : 'hover:bg-[#E4E4E7]'}`}
            >
              {isExpanded ? (
                <CaretDown size={14} className={isSelected ? 'text-white' : 'text-[#52525B]'} />
              ) : (
                <CaretRight size={14} className={isSelected ? 'text-white' : 'text-[#52525B]'} />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}
          {isExpanded ? (
            <FolderOpen size={18} className={isSelected ? 'text-white' : 'text-[#F5B041]'} />
          ) : (
            <Folder size={18} className={isSelected ? 'text-white' : 'text-[#F5B041]'} />
          )}
          <span className="text-sm font-medium truncate">{node.name}</span>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder size={20} className="text-[#F5B041]" />
            {t('documents.selectFolder')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="border border-[#E4E4E7] rounded-sm max-h-80 overflow-auto bg-white">
          <div
            className={`flex items-center gap-2 py-2 px-3 cursor-pointer rounded-sm transition-colors ${
              tempSelected === rootValue ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F4F4F5]'
            }`}
            onClick={() => setTempSelected(rootValue)}
          >
            <span className="w-5" />
            <House size={18} className={tempSelected === rootValue ? 'text-white' : 'text-[#52525B]'} />
            <span className="text-sm font-medium">{rootLabel}</span>
          </div>
          
          {treeData.map(node => renderTreeNode(node, 0))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm">
            <Check size={16} className="mr-2" />
            {t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Get file icon based on content type
const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return FilePdf;
  if (contentType?.includes('image')) return FileImage;
  if (contentType?.includes('text')) return FileText;
  return FileIcon;
};

export const EmailsPage = () => {
  const { t } = useLanguage();
  const [emails, setEmails] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');
  
  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTags, setUploadTags] = useState('');
  const [uploadFolder, setUploadFolder] = useState('none');
  const [uploading, setUploading] = useState(false);
  const [showFolderTreeDialog, setShowFolderTreeDialog] = useState(false);
  const [showFilterFolderDialog, setShowFilterFolderDialog] = useState(false);
  
  // Email detail dialog state
  const [viewEmail, setViewEmail] = useState(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [showHtmlBody, setShowHtmlBody] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  
  // Expanded rows state
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const [emailAttachmentsMap, setEmailAttachmentsMap] = useState({});
  const [loadingAttachmentsMap, setLoadingAttachmentsMap] = useState({});
  
  // Expanded embedded emails state
  const [expandedEmbeddedEmails, setExpandedEmbeddedEmails] = useState(new Set());
  const [embeddedEmailsData, setEmbeddedEmailsData] = useState({});
  const [embeddedEmailsAttachments, setEmbeddedEmailsAttachments] = useState({});
  const [loadingEmbeddedEmails, setLoadingEmbeddedEmails] = useState({});
  
  // Preview state
  const [previewEmail, setPreviewEmail] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  
  // Delete confirmation
  const [emailToDelete, setEmailToDelete] = useState(null);
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchEmails = useCallback(async () => {
    try {
      const res = await api.getEmails(selectedFolder, search);
      setEmails(res.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, search, t]);

  const fetchFolders = async () => {
    try {
      const res = await api.getAllFolders();
      setFolders(res.data);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEmails();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchEmails]);

  const getSelectedFolderName = () => {
    if (uploadFolder === 'none') return t('folders.root');
    const folder = folders.find(f => f.id === uploadFolder);
    return folder ? folder.name : t('folders.root');
  };

  const getFilterFolderName = () => {
    if (selectedFolder === 'all') return t('folders.root');
    const folder = folders.find(f => f.id === selectedFolder);
    return folder ? folder.name : t('folders.root');
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of uploadFiles) {
      try {
        await api.uploadEmail(file, uploadFolder === 'none' ? '' : uploadFolder, uploadTags);
        successCount++;
      } catch (error) {
        toast.error(`${t('common.error')}: ${file.name}`);
      }
    }
    
    if (successCount > 0) {
      toast.success(t('emails.uploadSuccess'));
      fetchEmails();
    }
    
    setShowUploadDialog(false);
    setUploadFiles([]);
    setUploadTags('');
    setUploadFolder('none');
    setUploading(false);
  };

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleViewEmail = async (email) => {
    try {
      const res = await api.getEmail(email.id);
      setViewEmail(res.data);
      setShowHtmlBody(!!res.data.body_html);
      
      // Load attachments
      if (email.attachments_count > 0) {
        setLoadingAttachments(true);
        const attRes = await api.getEmailAttachments(email.id);
        setEmailAttachments(attRes.data);
        setLoadingAttachments(false);
      } else {
        setEmailAttachments([]);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteEmail = async () => {
    if (!emailToDelete) return;
    
    try {
      await api.deleteEmail(emailToDelete.id);
      toast.success(t('common.success'));
      fetchEmails();
      setEmailToDelete(null);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const toggleEmailExpand = async (email) => {
    const emailId = email.id;
    
    if (expandedEmails.has(emailId)) {
      // Collapse
      setExpandedEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(emailId);
        return newSet;
      });
    } else {
      // Expand and load attachments if not already loaded
      setExpandedEmails(prev => new Set(prev).add(emailId));
      
      if (!emailAttachmentsMap[emailId] && email.attachments_count > 0) {
        setLoadingAttachmentsMap(prev => ({ ...prev, [emailId]: true }));
        try {
          const res = await api.getEmailAttachments(emailId);
          setEmailAttachmentsMap(prev => ({ ...prev, [emailId]: res.data }));
        } catch (error) {
          console.error('Error loading attachments:', error);
        } finally {
          setLoadingAttachmentsMap(prev => ({ ...prev, [emailId]: false }));
        }
      }
    }
  };

  const toggleEmbeddedEmailExpand = async (embeddedEmailId) => {
    if (expandedEmbeddedEmails.has(embeddedEmailId)) {
      // Collapse
      setExpandedEmbeddedEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(embeddedEmailId);
        return newSet;
      });
    } else {
      // Expand and load embedded email data
      setExpandedEmbeddedEmails(prev => new Set(prev).add(embeddedEmailId));
      
      if (!embeddedEmailsData[embeddedEmailId]) {
        setLoadingEmbeddedEmails(prev => ({ ...prev, [embeddedEmailId]: true }));
        try {
          // Get embedded email details
          const emailRes = await api.getEmbeddedEmail(embeddedEmailId);
          setEmbeddedEmailsData(prev => ({ ...prev, [embeddedEmailId]: emailRes.data }));
          
          // Get its attachments
          if (emailRes.data.attachments_count > 0) {
            const attRes = await api.getEmailAttachments(embeddedEmailId);
            setEmbeddedEmailsAttachments(prev => ({ ...prev, [embeddedEmailId]: attRes.data }));
          }
        } catch (error) {
          console.error('Error loading embedded email:', error);
          toast.error(t('common.error'));
        } finally {
          setLoadingEmbeddedEmails(prev => ({ ...prev, [embeddedEmailId]: false }));
        }
      }
    }
  };

  const handleDownloadAttachment = (att) => {
    window.open(api.downloadEmailAttachment(att.id), '_blank');
  };

  const handleDownloadAttachmentPdfa = (att) => {
    // For PDF/A we use the preview URL which can also serve as download
    window.open(api.getEmailAttachmentPreviewPdfaUrl(att.id), '_blank');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedEmails = useMemo(() => {
    return [...emails].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'subject' || sortField === 'from_address') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [emails, sortField, sortDirection]);

  const SortHeader = ({ field, children }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#52525B] cursor-pointer hover:bg-[#E4E4E7] transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
        ) : (
          <CaretUpDown size={14} className="text-[#A1A1AA]" />
        )}
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#18181B]">{t('emails.title')}</h1>
            <p className="text-sm text-[#52525B] mt-1">{t('emails.subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowUploadDialog(true)}
            className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            data-testid="upload-email-btn"
          >
            <UploadSimple size={18} className="mr-2" />
            {t('emails.upload')}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              placeholder={t('emails.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-swiss"
              data-testid="email-search"
            />
          </div>
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFilterFolderDialog(true)}
            className="w-[200px] justify-start rounded-sm h-10 text-left font-normal"
            data-testid="folder-filter-btn"
          >
            <Folder size={16} className="mr-2 text-[#F5B041]" />
            <span className="truncate flex-1">{getFilterFolderName()}</span>
            <CaretRight size={14} className="ml-auto text-[#A1A1AA]" />
          </Button>
        </div>

        {/* Emails Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F4F4F5]">
              <tr>
                <SortHeader field="subject">{t('emails.subject')}</SortHeader>
                <SortHeader field="from_address">{t('emails.from')}</SortHeader>
                <SortHeader field="date">{t('emails.date')}</SortHeader>
                <SortHeader field="attachments_count">{t('emails.attachments')}</SortHeader>
                <SortHeader field="size">{t('documents.size')}</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#52525B]">
                  PDF/A
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[#52525B]">
                  {t('documents.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E4E7]">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[#52525B]">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : sortedEmails.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[#52525B]">
                    {t('emails.noEmails')}
                  </td>
                </tr>
              ) : (
                sortedEmails.map((email) => {
                  const isExpanded = expandedEmails.has(email.id);
                  const attachments = emailAttachmentsMap[email.id] || [];
                  const isLoadingAtt = loadingAttachmentsMap[email.id];
                  
                  return (
                    <React.Fragment key={email.id}>
                      {/* Email Row */}
                      <tr className={`hover:bg-[#F4F4F5] transition-colors ${isExpanded ? 'bg-[#F4F4F5]' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {email.attachments_count > 0 && (
                              <button
                                onClick={() => toggleEmailExpand(email)}
                                className="p-0.5 hover:bg-[#E4E4E7] rounded-sm"
                              >
                                {isExpanded ? (
                                  <CaretDown size={16} className="text-[#52525B]" />
                                ) : (
                                  <CaretRight size={16} className="text-[#52525B]" />
                                )}
                              </button>
                            )}
                            {email.attachments_count === 0 && <span className="w-5" />}
                            <Envelope size={18} className="text-[#002FA7]" />
                            <span className="text-sm font-medium text-[#18181B] truncate max-w-xs">
                              {email.subject}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#52525B] truncate max-w-[200px]">
                          {email.from_address}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#52525B]">
                          {email.date ? formatDate(email.date) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {email.attachments_count > 0 ? (
                            <button
                              onClick={() => toggleEmailExpand(email)}
                              className="flex items-center gap-1 text-sm text-[#002FA7] hover:underline"
                            >
                              <Paperclip size={14} />
                              <span>{email.attachments_count}</span>
                            </button>
                          ) : (
                            <span className="text-sm text-[#A1A1AA]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#52525B]">
                          {formatBytes(email.size)}
                        </td>
                        <td className="px-4 py-3">
                          {email.has_pdfa ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                              <Check size={12} weight="bold" />
                              PDF/A
                            </span>
                          ) : (
                            <span className="text-[#A1A1AA]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewEmail(email)}
                              title={t('common.view')}
                              className="h-8 w-8 p-0"
                            >
                              <Info size={16} />
                            </Button>
                            {email.has_pdfa && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(api.getEmailPreviewPdfaUrl(email.id), '_blank')}
                                title={t('documents.preview') + ' PDF/A'}
                                className="h-8 w-8 p-0 text-green-700"
                              >
                                <Eye size={16} />
                              </Button>
                            )}
                            <a
                              href={api.downloadEmail(email.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-[#F4F4F5]"
                              title={t('common.download')}
                            >
                              <DownloadSimple size={16} />
                            </a>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <DotsThreeVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEmailToDelete(email)} className="text-red-600">
                                  <Trash size={16} className="mr-2" />
                                  {t('common.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Attachments Row */}
                      {isExpanded && (
                        <tr className="bg-[#FAFAFA]">
                          <td colSpan={7} className="px-4 py-3">
                            <div className="ml-6 border-l-2 border-[#002FA7] pl-4">
                              <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-3 flex items-center gap-2">
                                <Paperclip size={14} />
                                {t('emails.attachments')} ({email.attachments_count})
                              </h4>
                              
                              {isLoadingAtt ? (
                                <p className="text-sm text-[#52525B]">{t('common.loading')}</p>
                              ) : attachments.length === 0 ? (
                                <p className="text-sm text-[#A1A1AA]">{t('emails.noAttachments')}</p>
                              ) : (
                                <table className="w-full">
                                  <thead className="bg-[#F4F4F5]">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-[#52525B]">
                                        {t('documents.name')}
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-[#52525B]">
                                        {t('documents.size')}
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-[#52525B]">
                                        {t('documents.documentType')}
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-[#52525B]">
                                        PDF/A
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-[#52525B]">
                                        {t('documents.date')}
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-[#52525B]">
                                        {t('documents.actions')}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-[#E4E4E7] bg-white">
                                    {attachments.map((att) => {
                                      const FileTypeIcon = att.is_email ? Envelope : getFileIcon(att.content_type);
                                      const isEmbeddedEmailExpanded = att.is_email && expandedEmbeddedEmails.has(att.embedded_email_id);
                                      const embeddedEmail = embeddedEmailsData[att.embedded_email_id];
                                      const embeddedAttachments = embeddedEmailsAttachments[att.embedded_email_id] || [];
                                      
                                      return (
                                        <React.Fragment key={att.id}>
                                          <tr className={`hover:bg-[#F4F4F5] ${att.is_email ? 'bg-blue-50/30' : ''}`}>
                                            <td className="px-3 py-2">
                                              <div className="flex items-center gap-2">
                                                {att.is_email && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 w-5 p-0 mr-1"
                                                    onClick={() => toggleEmbeddedEmailExpand(att.embedded_email_id)}
                                                  >
                                                    {isEmbeddedEmailExpanded ? (
                                                      <CaretDown size={14} weight="bold" />
                                                    ) : (
                                                      <CaretRight size={14} weight="bold" />
                                                    )}
                                                  </Button>
                                                )}
                                                <FileTypeIcon size={16} className={att.is_email ? 'text-blue-600' : 'text-[#52525B]'} />
                                                <span className={`text-sm font-medium truncate max-w-[200px] ${att.is_email ? 'text-blue-700' : ''}`}>
                                                  {att.filename}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="px-3 py-2 text-sm text-[#52525B]">
                                              {formatBytes(att.size)}
                                            </td>
                                            <td className="px-3 py-2">
                                              <span className={`text-sm ${att.is_email ? 'text-blue-600 font-medium' : 'text-[#52525B]'}`}>
                                                {att.is_email ? 'Email' : (att.document_type_label || 'Altro')}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2">
                                              {att.has_pdfa ? (
                                                <div className="flex items-center gap-1">
                                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                                                    <Check size={12} weight="bold" />
                                                    PDF/A
                                                  </span>
                                                  {att.pdfa_size && (
                                                    <span className="text-xs text-[#A1A1AA]">
                                                      ({formatBytes(att.pdfa_size)})
                                                    </span>
                                                  )}
                                                </div>
                                              ) : (
                                                <span className="text-[#A1A1AA]">-</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-[#52525B]">
                                              {formatDate(att.created_at)}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                              <div className="flex items-center justify-end gap-1">
                                                {att.is_email && att.embedded_email_id && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      const embEmail = embeddedEmailsData[att.embedded_email_id];
                                                      if (embEmail) {
                                                        setPreviewEmail(embEmail);
                                                      } else {
                                                        // Load it first
                                                        api.getEmbeddedEmail(att.embedded_email_id).then(res => {
                                                          setEmbeddedEmailsData(prev => ({ ...prev, [att.embedded_email_id]: res.data }));
                                                          setPreviewEmail(res.data);
                                                        });
                                                      }
                                                    }}
                                                    title={t('documents.preview')}
                                                    className="h-7 w-7 p-0 text-blue-600"
                                                  >
                                                    <Eye size={14} />
                                                  </Button>
                                                )}
                                                {att.has_pdfa && !att.is_email && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setPreviewAttachment(att)}
                                                    title={t('documents.preview')}
                                                    className="h-7 w-7 p-0"
                                                  >
                                                    <Eye size={14} />
                                                  </Button>
                                                )}
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => handleDownloadAttachment(att)}
                                                  title={t('common.download')}
                                                  className="h-7 w-7 p-0"
                                                >
                                                  <DownloadSimple size={14} />
                                                </Button>
                                                {att.has_pdfa && !att.is_email && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownloadAttachmentPdfa(att)}
                                                    title={t('documents.downloadPdfa')}
                                                    className="h-7 px-2 text-xs text-green-700"
                                                  >
                                                    <Archive size={14} className="mr-1" />
                                                    PDF/A
                                                  </Button>
                                                )}
                                                {att.is_email && att.embedded_email_id && embeddedEmailsData[att.embedded_email_id]?.has_pdfa && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      const embEmail = embeddedEmailsData[att.embedded_email_id];
                                                      if (embEmail) {
                                                        window.open(api.getEmailPreviewPdfaUrl(embEmail.id), '_blank');
                                                      }
                                                    }}
                                                    title={t('documents.downloadPdfa')}
                                                    className="h-7 px-2 text-xs text-green-700"
                                                  >
                                                    <Archive size={14} className="mr-1" />
                                                    PDF/A
                                                  </Button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                          
                                          {/* Expanded embedded email content */}
                                          {att.is_email && isEmbeddedEmailExpanded && (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-3 bg-blue-50/50">
                                                {loadingEmbeddedEmails[att.embedded_email_id] ? (
                                                  <div className="flex items-center justify-center py-4">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                                  </div>
                                                ) : embeddedEmail ? (
                                                  <div className="ml-6 space-y-3">
                                                    {/* Embedded email header */}
                                                    <div className="bg-white rounded-lg border border-blue-200 p-3">
                                                      <div className="flex items-start gap-3">
                                                        <Envelope size={20} className="text-blue-600 mt-0.5" />
                                                        <div className="flex-1 min-w-0">
                                                          <h4 className="font-semibold text-sm text-gray-900 truncate">
                                                            {embeddedEmail.subject || 'No Subject'}
                                                          </h4>
                                                          <p className="text-xs text-gray-600 mt-1">
                                                            <span className="font-medium">{t('emails.from')}:</span> {embeddedEmail.from_address}
                                                          </p>
                                                          <p className="text-xs text-gray-600">
                                                            <span className="font-medium">{t('emails.to')}:</span> {embeddedEmail.to_addresses?.join(', ') || '-'}
                                                          </p>
                                                          {embeddedEmail.date && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                              {formatDate(embeddedEmail.date)}
                                                            </p>
                                                          )}
                                                          <div className="flex items-center gap-2 mt-2">
                                                            {embeddedEmail.has_pdfa && (
                                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                                                                <Check size={12} weight="bold" />
                                                                PDF/A
                                                              </span>
                                                            )}
                                                            {embeddedEmail.attachments_count > 0 && (
                                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                                                                <Paperclip size={12} />
                                                                {embeddedEmail.attachments_count}
                                                              </span>
                                                            )}
                                                          </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setPreviewEmail(embeddedEmail)}
                                                            title={t('documents.preview')}
                                                            className="h-7 w-7 p-0"
                                                          >
                                                            <Eye size={14} />
                                                          </Button>
                                                          {embeddedEmail.has_pdfa && (
                                                            <Button
                                                              variant="ghost"
                                                              size="sm"
                                                              onClick={() => window.open(api.getEmailPreviewPdfaUrl(embeddedEmail.id), '_blank')}
                                                              title="PDF/A"
                                                              className="h-7 px-2 text-xs text-green-700"
                                                            >
                                                              <Archive size={14} className="mr-1" />
                                                              PDF/A
                                                            </Button>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </div>
                                                    
                                                    {/* Embedded email's attachments */}
                                                    {embeddedAttachments.length > 0 && (
                                                      <div className="ml-4">
                                                        <p className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                                                          <Paperclip size={12} />
                                                          {t('emails.attachments')} ({embeddedAttachments.length})
                                                        </p>
                                                        <div className="space-y-1">
                                                          {embeddedAttachments.map((embAtt) => {
                                                            const EmbFileIcon = embAtt.is_email ? Envelope : getFileIcon(embAtt.content_type);
                                                            return (
                                                              <div key={embAtt.id} className="flex items-center justify-between bg-white rounded border border-gray-200 px-2 py-1.5">
                                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                  <EmbFileIcon size={14} className={embAtt.is_email ? 'text-blue-600' : 'text-gray-500'} />
                                                                  <span className="text-xs truncate">{embAtt.filename}</span>
                                                                  <span className="text-xs text-gray-400">({formatBytes(embAtt.size)})</span>
                                                                  {embAtt.has_pdfa && (
                                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-medium">
                                                                      <Check size={10} weight="bold" />
                                                                      PDF/A
                                                                    </span>
                                                                  )}
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                  <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleDownloadAttachment(embAtt)}
                                                                    title={t('common.download')}
                                                                    className="h-6 w-6 p-0"
                                                                  >
                                                                    <DownloadSimple size={12} />
                                                                  </Button>
                                                                </div>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                ) : null}
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Email Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Envelope size={20} />
              {t('emails.upload')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Drop zone */}
            <div 
              className="border-2 border-dashed border-[#E4E4E7] rounded-sm p-8 text-center hover:border-[#002FA7] transition-colors cursor-pointer"
              onClick={() => document.getElementById('email-file-input').click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#002FA7]', 'bg-[#F4F4F5]'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('border-[#002FA7]', 'bg-[#F4F4F5]'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-[#002FA7]', 'bg-[#F4F4F5]');
                const files = Array.from(e.dataTransfer.files).filter(
                  f => f.name.endsWith('.eml') || f.name.endsWith('.msg')
                );
                setUploadFiles(prev => [...prev, ...files]);
              }}
            >
              <Envelope size={32} className="mx-auto text-[#A1A1AA] mb-2" />
              <p className="text-sm text-[#52525B]">{t('emails.dropFiles')}</p>
              <p className="text-xs text-[#A1A1AA] mt-1">{t('emails.supportedFormats')}</p>
              <input
                id="email-file-input"
                type="file"
                multiple
                accept=".eml,.msg"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setUploadFiles(prev => [...prev, ...files]);
                  e.target.value = '';
                }}
              />
            </div>

            {/* File list */}
            {uploadFiles.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-auto">
                {uploadFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[#F4F4F5] rounded-sm">
                    <div className="flex items-center gap-2">
                      <Envelope size={16} className="text-[#52525B]" />
                      <span className="text-sm truncate max-w-xs">{file.name}</span>
                      <span className="text-xs text-[#A1A1AA]">({formatBytes(file.size)})</span>
                    </div>
                    <button onClick={() => removeUploadFile(idx)} className="p-1 hover:bg-[#E4E4E7] rounded-sm">
                      <X size={14} className="text-[#52525B]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Folder Selection */}
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
                {t('documents.selectFolder')}
              </Label>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFolderTreeDialog(true)}
                className="w-full justify-start rounded-sm h-10 text-left font-normal"
              >
                <Folder size={16} className="mr-2 text-[#F5B041]" />
                <span className="truncate">{getSelectedFolderName()}</span>
                <CaretRight size={14} className="ml-auto text-[#A1A1AA]" />
              </Button>
            </div>

            {/* Tags */}
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('documents.tags')}
              </Label>
              <Input
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder={t('documents.tagsPlaceholder')}
                className="input-swiss mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="rounded-sm">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || uploadFiles.length === 0}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            >
              {uploading ? t('common.loading') : `${t('emails.upload')} (${uploadFiles.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Tree Selection Dialog for Upload */}
      <FolderTreeDialog
        open={showFolderTreeDialog}
        onClose={() => setShowFolderTreeDialog(false)}
        folders={folders}
        selectedFolder={uploadFolder}
        onSelect={setUploadFolder}
        rootLabel={t('folders.root')}
        rootValue="none"
        t={t}
      />

      {/* Folder Tree Selection Dialog for Filter */}
      <FolderTreeDialog
        open={showFilterFolderDialog}
        onClose={() => setShowFilterFolderDialog(false)}
        folders={folders}
        selectedFolder={selectedFolder}
        onSelect={setSelectedFolder}
        rootLabel={t('folders.root')}
        rootValue="all"
        t={t}
      />

      {/* View Email Dialog */}
      <Dialog open={!!viewEmail} onOpenChange={() => setViewEmail(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeOpen size={20} className="text-[#002FA7]" />
              {viewEmail?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {viewEmail && (
            <div className="flex-1 overflow-auto space-y-4">
              {/* Email metadata */}
              <div className="p-4 bg-[#F4F4F5] rounded-sm space-y-2">
                <div className="flex items-start gap-2">
                  <User size={16} className="text-[#52525B] mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-[#52525B] uppercase">{t('emails.from')}:</span>
                    <p className="text-sm">{viewEmail.from_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users size={16} className="text-[#52525B] mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-[#52525B] uppercase">{t('emails.to')}:</span>
                    <p className="text-sm">{viewEmail.to_addresses?.join(', ') || '-'}</p>
                  </div>
                </div>
                {viewEmail.cc_addresses?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Users size={16} className="text-[#52525B] mt-0.5" />
                    <div>
                      <span className="text-xs font-medium text-[#52525B] uppercase">CC:</span>
                      <p className="text-sm">{viewEmail.cc_addresses.join(', ')}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Calendar size={16} className="text-[#52525B] mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-[#52525B] uppercase">{t('emails.date')}:</span>
                    <p className="text-sm">{viewEmail.date ? formatDate(viewEmail.date) : '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Technical details - like documents */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-white border border-[#E4E4E7] rounded-sm">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.size')}
                  </p>
                  <p className="text-sm text-[#09090B]">{formatBytes(viewEmail.size)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.date')}
                  </p>
                  <p className="text-sm text-[#09090B]">{formatDate(viewEmail.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    File
                  </p>
                  <p className="text-sm text-[#09090B] truncate" title={viewEmail.original_filename}>
                    {viewEmail.original_filename}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    PDF/A
                  </p>
                  {viewEmail.has_pdfa ? (
                    <div className="flex items-center gap-1">
                      <CheckCircle size={16} className="text-green-600" weight="fill" />
                      <span className="text-sm text-green-700">
                        {viewEmail.pdfa_size ? formatBytes(viewEmail.pdfa_size) : 'OK'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">-</span>
                  )}
                </div>
              </div>
              
              {/* Hash SHA-256 */}
              {viewEmail.hash_sha256 && (
                <div className="p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.hash')}
                  </p>
                  <p className="hash-display text-xs break-all font-mono text-[#52525B]">
                    {viewEmail.hash_sha256}
                  </p>
                </div>
              )}

              {/* Body toggle */}
              {viewEmail.body_html && viewEmail.body_text && (
                <div className="flex items-center gap-2">
                  <Button
                    variant={showHtmlBody ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowHtmlBody(true)}
                    className="rounded-sm"
                  >
                    HTML
                  </Button>
                  <Button
                    variant={!showHtmlBody ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowHtmlBody(false)}
                    className="rounded-sm"
                  >
                    {t('emails.plainText')}
                  </Button>
                </div>
              )}

              {/* Email body */}
              <div className="border border-[#E4E4E7] rounded-sm p-4 bg-white min-h-[200px] max-h-[300px] overflow-auto">
                {showHtmlBody && viewEmail.body_html ? (
                  <div dangerouslySetInnerHTML={{ __html: viewEmail.body_html }} className="prose prose-sm max-w-none" />
                ) : (
                  <pre className="text-sm whitespace-pre-wrap font-sans">{viewEmail.body_text || t('emails.noContent')}</pre>
                )}
              </div>

              {/* Attachments */}
              {viewEmail.attachments_count > 0 && (
                <div className="p-4 bg-[#F4F4F5] rounded-sm">
                  <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-3 flex items-center gap-2">
                    <Paperclip size={14} />
                    {t('emails.attachments')} ({viewEmail.attachments_count})
                  </h3>
                  
                  {loadingAttachments ? (
                    <p className="text-sm text-[#52525B]">{t('common.loading')}</p>
                  ) : (
                    <div className="space-y-2">
                      {emailAttachments.map((att) => {
                        const FileTypeIcon = getFileIcon(att.content_type);
                        return (
                          <div key={att.id} className="flex items-center justify-between p-2 bg-white rounded-sm">
                            <div className="flex items-center gap-2">
                              <FileTypeIcon size={18} className="text-[#52525B]" />
                              <span className="text-sm font-medium">{att.filename}</span>
                              <span className="text-xs text-[#A1A1AA]">({formatBytes(att.size)})</span>
                              {att.has_pdfa && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-xs">
                                  PDF/A
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {att.has_pdfa && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPreviewAttachment(att)}
                                  className="h-7 px-2"
                                >
                                  <Eye size={14} className="mr-1" />
                                  {t('documents.preview')}
                                </Button>
                              )}
                              <a
                                href={api.downloadEmailAttachment(att.id)}
                                className="inline-flex items-center justify-center h-7 px-2 text-sm rounded-md hover:bg-[#F4F4F5]"
                              >
                                <DownloadSimple size={14} className="mr-1" />
                                {t('common.download')}
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* PDF/A Preview */}
              {viewEmail.has_pdfa && (
                <div className="p-3 bg-green-50 rounded-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Archive size={18} className="text-green-600" />
                    <span className="text-sm font-medium text-green-700">{t('documents.pdfaAvailable')}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewEmail(viewEmail)}
                    className="rounded-sm border-green-600 text-green-700 hover:bg-green-100"
                  >
                    <Eye size={14} className="mr-1" />
                    {t('documents.preview')}
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewEmail(null)} className="rounded-sm">
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email PDF/A Preview Dialog */}
      <Dialog open={!!previewEmail} onOpenChange={() => setPreviewEmail(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={20} className="text-green-600" />
              {previewEmail?.subject} - PDF/A
            </DialogTitle>
          </DialogHeader>
          
          <div className="min-h-[400px] max-h-[60vh] overflow-auto bg-[#F4F4F5] rounded-sm flex items-center justify-center">
            {previewEmail && (
              <iframe
                src={api.getEmailPreviewPdfaUrl(previewEmail.id)}
                className="w-full h-[60vh] border-0"
                title={`${previewEmail.subject} - PDF/A`}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewEmail(null)} className="rounded-sm">
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachment PDF/A Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={() => setPreviewAttachment(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={20} className="text-green-600" />
              {previewAttachment?.filename} - PDF/A
            </DialogTitle>
          </DialogHeader>
          
          <div className="min-h-[400px] max-h-[60vh] overflow-auto bg-[#F4F4F5] rounded-sm flex items-center justify-center">
            {previewAttachment && (
              <iframe
                src={api.getEmailAttachmentPreviewPdfaUrl(previewAttachment.id)}
                className="w-full h-[60vh] border-0"
                title={`${previewAttachment.filename} - PDF/A`}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewAttachment(null)} className="rounded-sm">
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!emailToDelete} onOpenChange={() => setEmailToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emails.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emails.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEmail} className="bg-red-600 hover:bg-red-700 rounded-sm">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default EmailsPage;
